import React, { useEffect, useState, useContext } from "react";
import Navbar from "../components/common/Navbar";
import StarredTable from "../components/starred-document-table/StarredTable";
import { AuthContext } from "../context/Auth/AuthContext";
import starredDocuments from "../api/starred";
import baseUrl from "../config/baseUrl";

import {
  CaretSortIcon,
  ChevronDownIcon,
  StarIcon,
  StarFilledIcon,
} from "@radix-ui/react-icons";

import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Button } from "../shadcn-components/ui/button";
import { Checkbox } from "../shadcn-components/ui/checkbox";
import { fileIcon } from "../components/common/FileIcons";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../shadcn-components/ui/dropdown-menu";
import "../styles/Starred.css";
import "../styles/Documents.css";
import removeDocumentFromFavourite from "../api/removeFavouriteDocument";
import { useDebounce } from "use-debounce";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import PageInfoTooltip from "../components/common/PageInfoTooltip";
import PdfViewer from "../components/pdf-viewer/PdfViewer";
import DocumentsPageHeader from "../components/common/DocumentsPageHeader";
import documentData from "../api/documents";
import { showError } from "../utils/toast";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

/*** Main component for listing all the starred documents ***/
const Starred = () => {
  const [loading, setLoading] = useState(true);
  const [starredDocumentsResponse, setStarredDocumentsResponse] = useState([]);
  const [documentFavouriteState, setDocumentFavouriteState] = useState(0);
  const { token, user_id, is_admin, id: userId } = useContext(AuthContext);

  // Opened document (same Document Record / PDF viewer as All Documents).
  const [documentDetails, setDocumentDetails] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedGlobalFilter] = useDebounce(globalFilter, 1000);

  // silent: refresh the list without the loading state (used right after a
  // favourite is removed, when the rows were already updated locally).
  const fetchStarredDocuments = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const offset = currentPage - 1;
    const filterParams = {};

    columnFilters.forEach((filterItem) => {
      filterParams[filterItem.id] = filterItem.value;
    });

    const sortParam = sorting
      .map((sortItem) => {
        return `${sortItem.id}=${sortItem.desc ? "desc" : "asc"}`;
      })
      .join(",");

    const params = {
      isAdmin: 1,
      userId: 1,
      limit: pageSize,
      offset: offset,
      ...filterParams,
      global_search: debouncedGlobalFilter,
      sort: sortParam,
    };
    const queryString = new URLSearchParams(params).toString();
    const url = `${baseUrl}/api/get-fav-data?${queryString}`;
    const response = await starredDocuments({ token, api: url });

    if (response.code == 200) {
      setStarredDocumentsResponse(response?.data);
      setTotalPages(Math.ceil(response?.user_count / pageSize));
    } else {
      setStarredDocumentsResponse([]);
      setTotalPages(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStarredDocuments();
  }, [
    documentFavouriteState,
    debouncedGlobalFilter,
    currentPage,
    columnFilters,
    sorting,
    pageSize,
  ]);

  const removeDocumentsFromFavouriteList = (documentList) => {
    setDocumentFavouriteState((count) => count + 1);
  };

  // After the existing remove-bulk-all-favourite call succeeds: drop those
  // documents from the table right away, clear the selection, then refresh
  // quietly so the page is refilled and the page count stays correct.
  const syncAfterRemoval = (removedIds) => {
    const removed = new Set(removedIds.map(String));
    const remaining = (starredDocumentsResponse || []).filter(
      (doc) => !removed.has(String(doc.document_id))
    );
    setStarredDocumentsResponse(remaining);
    setRowSelection({});
    if (remaining.length === 0 && currentPage > 1) {
      setCurrentPage((page) => page - 1); // triggers the normal fetch
    } else {
      fetchStarredDocuments({ silent: true });
    }
  };

  // Same lookup as the All Documents "openDocument" deep link: get-fav-data
  // rows don't carry the full document record PdfViewer needs, so load that
  // one row with the existing get-all-doc-data API, then open it.
  const openDocument = async (row) => {
    const documentId = row?.document_id;
    if (!documentId) return;
    const params = new URLSearchParams({
      document_id: documentId,
      isAdmin: is_admin,
      userId,
      ...(is_admin === "1" && localStorage.getItem("role_id")
        ? { roleId: localStorage.getItem("role_id") }
        : {}),
    });
    const res = await documentData({
      token,
      api: `${baseUrl}/api/get-all-doc-data?${params.toString()}`,
    });
    const doc = (res?.data?.[0]?.document || []).find(
      (d) => String(d.document_id) === String(documentId)
    );
    if (doc) {
      setDocumentDetails(doc);
    } else {
      showError(
        "This document could not be opened. It may have been deleted or you may not have access to it."
      );
    }
  };

  // Closing the viewer returns to the Favourites list (refreshed quietly, in
  // case the document was un-starred or changed in the viewer).
  const closeDocument = () => {
    setDocumentDetails(null);
    fetchStarredDocuments({ silent: true });
  };
  const refetchDocumentTable = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  const removedOk = (res) =>
    res?.status === "success" || res?.code === 200 || res?.code === "200";

  const handleRemoveFavourites = async (row) => {
    const { document_id } = row;
    try {
      const res = await removeDocumentFromFavourite({ token, ids: document_id });
      if (removedOk(res)) syncAfterRemoval([document_id]);
    } catch (err) {
    }
  };

  const handleBulkAction = async (actionType) => {
    const selectedRowsData = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original);
    const idList = selectedRowsData.map((row) => row.document_id);
    const ids = idList.join(",");
    if (actionType === "favourite" && idList.length) {
      const res = await removeDocumentFromFavourite({ token, ids: ids });
      if (removedOk(res)) syncAfterRemoval(idList);
    }
  };

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <div className="flex items-center">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
          {(table.getIsSomePageRowsSelected() ||
            table.getIsAllPageRowsSelected()) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="p-0 !border-none !shadow-none bg-transparent"
                >
                  <ChevronDownIcon className="" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => handleBulkAction("favourite")}
                >
                  Remove Favourites
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "favourite",
      header: ({ table }) => <StarFilledIcon className="text-yellow-500" />,
      // get-fav-data returns favourite as a number (1) here, so compare as a
      // string - same filled yellow star / outline star as All Documents.
      cell: ({ row }) =>
        String(row.getValue("favourite")) === "1" ? (
          <StarFilledIcon
            className="text-yellow-500 cursor-pointer"
            onClick={() => handleRemoveFavourites(row.original)}
          />
        ) : (
          <StarIcon className="cursor-pointer" />
        ),
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      accessorKey: "name",
      displayName: "Client Name",
      filterable: true,
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Client Name
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("name")}</div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "document_type",
      filterable: true,
      displayName: "Type",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Type
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="w-full flex items-center">
          {fileIcon[row.getValue("document_type").toLowerCase()]}
        </div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "document_name",
      filterable: true,
      displayName: "Document Name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Document Name
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      // Clickable like the All Documents list - opens the existing viewer.
      cell: ({ row }) => (
        <div
          className="wrap-text capitalize text-left underline cursor-pointer text-blue-500"
          onClick={() => openDocument(row.original)}
        >
          {row.getValue("document_name")}
        </div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    // {
    //   accessorKey: "document_status",
    //   displayName: "Status",
    //   header: ({ column }) => (
    //     <Button
    //       variant="ghost"
    //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    //       className="p-0"
    //     >
    //       Status
    //       <CaretSortIcon className="ml-2 h-4 w-4" />
    //     </Button>
    //   ),
    //   cell: ({ row }) => (
    //     <div className="text-left">
    //       {row.getValue("document_status").toLowerCase() === "active"
    //         ? "Active"
    //         : "Inactive"}
    //     </div>
    //   ),
    //   filterFn: (row, id, value) => {
    //     return value.includes(row.getValue(id));
    //   },
    // },
    {
      accessorKey: "created_by_name",
      displayName: "Uploaded By",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="p-0"
        >
          Uploaded By
          <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("created_by_name")}</div>
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
  ];

  const table = useReactTable({
    data: starredDocumentsResponse,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      pageSize: 20,
    },
    manualPagination: true,
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pageSize,
    },

    globalFilterFn: (rows, columnFilters) => {
      const [globalFilter] = columnFilters.filter(
        (filter) => filter.id === "global"
      );
      if (!globalFilter || !globalFilter.value) return rows;

      return rows.filter((row) => {
        const name = row.original.name.toLowerCase(); // Assuming 'name' is the column key
        return name.includes(globalFilter.value.toLowerCase());
      });
    },
    onGlobalFilterChange: (filterValue) => {
      setGlobalFilter(filterValue); // Update the global filter state
    },
  });

  return (
    <div className="starred-container overflow-auto">
      <div className="navbar">
        <Navbar />
      </div>
      {documentDetails ? (
        <div className="starred-content">
          <DocumentsPageHeader
            showHeader={false}
            breadcrumbs={[
              { label: "Favourites", onClick: closeDocument },
              {
                label:
                  documentDetails.update_document_name ||
                  documentDetails.document_name,
              },
            ]}
          />
          <DndProvider backend={HTML5Backend}>
            <PdfViewer
              documentType={documentDetails.document_type}
              clientID={documentDetails.contact_id}
              documentID={documentDetails.document_id}
              documentTitle={documentDetails.update_document_name}
              documentCreatedAt={documentDetails.created_at}
              documentURL={documentDetails.document_url}
              changeActiveTab={closeDocument}
              document_table="documents"
              document_version={documentDetails.document_version}
              documentRequestID={documentDetails.document_request_id}
              existingDocumentRequestStatus={
                documentDetails.document_request_status
              }
              documentCategory={documentDetails.doc_category}
              documentDesc={documentDetails.document_desc}
              currentTabId={documentDetails.document_id}
              closeTabById={closeDocument}
              setDocumentDetails={setDocumentDetails}
              fromTable="Documents"
              setUpdatedFromPDF={() => {}}
              linkedDocReqName={documentDetails.document_request_setting_name}
              documentSubCategory={documentDetails.document_sub_category}
              clientPhone={documentDetails.phone_number}
              benID={documentDetails.ben_id}
              refetchDocumentTable={refetchDocumentTable}
              refetchTrigger={refetchTrigger}
            />
          </DndProvider>
        </div>
      ) : (
      <div className="starred-content">
        {/* No breadcrumb - Favourites is a plain list page with no deeper
            detail/viewer state, and the Sidebar already shows the current
            location. */}
        <div className="documents-page-header flex items-center justify-between gap-3 py-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-nbl-primary text-white">
              <StarBorderOutlinedIcon sx={{ fontSize: 18 }} />
            </span>
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 min-w-0">
                <h1 className="text-lg font-semibold text-gray-900 leading-tight truncate min-w-0">
                  Favourites
                </h1>
                <PageInfoTooltip title="Favourites" description="Documents you have starred" />
              </span>
              <p className="text-xs text-gray-500 leading-tight truncate">
                Documents you have starred
              </p>
            </span>
          </div>
        </div>
        <div className="starred-table-content">
          <StarredTable
            table={table}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            columns={columns}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            totalPages={totalPages}
            removeDocumentsFromFavouriteList={removeDocumentsFromFavouriteList}
            starredDocumentsResponse={starredDocumentsResponse}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        </div>
      </div>
      )}
    </div>
  );
};

export default Starred;
